
import './synchro-modal.scss'

import { faBoxOpen, faCheck, faCloudDownloadAlt, faCloudUploadAlt, faEdit, faExclamationTriangle, faInfoCircle, faPlusCircle, faSlash, faSyncAlt, faTabletAlt, faTimes, faToggleOff, faToggleOn, faWifi } from '@fortawesome/free-solid-svg-icons';
import bModal from 'bootstrap-vue/es/components/modal/modal';
import moment from 'moment';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';

import { EntityFieldDataStore } from '@skinsoft/data-store/src/offline/entities-data-store/entity-field-data-store';
import { ReactiveVue } from '@skinsoft/reactive-vue';

import { DataStore, dataSynchroEventBus$ } from '@skinsoft/data-store';
import { DataSynchronisationStatus, DataSynchronizerStats, OfflineDataFetcher } from '@skinsoft/data-store/src/offline/offline-data-fetcher';
import { OfflineDataUpdateCacher } from '@skinsoft/data-store/src/offline/offline-data-update-cacher';
import { AliasDataStore, AuditCampaignDataStore, AuthorityDataStore, ExpressionDataStore, ItemDataStore, LocationDataStore, ManifestationDataStore, RecordDataStore, ThesaurusDataStore, ThesaurusTermDataStore, WorkDataStore } from '@skinsoft/entity';
import { formatDateFromNow, formatDuration } from '@skinsoft/ui';
import { merge, Observable } from 'rxjs';
import { OnlineStatusIndicator } from '../online-status-indicator';
import { SynchroView } from '../synchro-view';

@Component({
    components: {
        sOnlineStatusIndicator: OnlineStatusIndicator,
        sSynchroView: SynchroView
    },
    props: [''],
    template: require('./synchro-modal.html')
})
export class SynchroModal extends ReactiveVue {

    public $refs = {
        modal: bModal
    }

    // Synchro
    private nbSynchroCompleted: number = 0;
    private errorInGlobalSynchro: boolean = false;
    private progressBarVariant: string = "success";
    private globalSyncing: boolean = false;
    private executionStartTime: number = 0;
    private lastSynchroStats: DataSynchronizerStats = null as any;
    private showDownloadDetails: boolean = false;

    // Push
    private pushResults: any[] = [];
    private nbEntitiesInUpdateCache: number = 0;
    private pushAllStatus: DataSynchronisationStatus = DataSynchronisationStatus.SUCCESS;
    private nbPushesCompleted: number = 0;
    private nbSuccesfullPushes: number = 0;
    private lastPushStats: any = { date: 0, status: DataSynchronisationStatus.SUCCESS };
    private globalPushing: boolean = false;
    private showUploadDetails: boolean = false;
    private pushProgressBarVariant = "success";

    // Icons
    private syncIcon = faSyncAlt;
    private crossIcon = faTimes;
    private wifiIcon = faWifi;
    private slashIcon = faSlash;
    private downloadIcon = faCloudDownloadAlt;
    private uploadIcon = faCloudUploadAlt;
    private infoIcon = faInfoCircle;
    private warningIcon = faExclamationTriangle;
    private localIcon = faTabletAlt;
    private switchOnIcon = faToggleOn;
    private switchOffIcon = faToggleOff;
    private statusSuccessIcon = faCheck;
    private statusErrorIcon = faTimes;
    private emptyIcon = faBoxOpen;
    private editIcon = faEdit;
    private plusIcon = faPlusCircle;

    // TODO externalize for future offline apps
    private dataStores: Array<{ title: string, dataStore: DataStore<any>, useForFetching: boolean, pushNewEntities: boolean }> = [
        { title: "entityNames.items", dataStore: ItemDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        // pushNewEntities is false for this one because new records are managed by audit campaign update
        { title: "entityNames.auditRecords", dataStore: RecordDataStore.getInstance(), useForFetching: true, pushNewEntities: false },
        { title: "entityNames.audits", dataStore: AuditCampaignDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        { title: "entityNames.thesaurusTerms", dataStore: ThesaurusTermDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        { title: "entityNames.thesaurus", dataStore: ThesaurusDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        { title: "entityNames.entityFields", dataStore: EntityFieldDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        { title: "entityNames.aliases", dataStore: AliasDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        { title: "entityNames.authorities", dataStore: AuthorityDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        { title: "entityNames.locations", dataStore: LocationDataStore.getInstance(), useForFetching: true, pushNewEntities: true },
        // datastores used only for pushing cached data (WEMI)
        { title: "manifestations", dataStore: ManifestationDataStore.getInstance(), useForFetching: false, pushNewEntities: true },
        { title: "expressions", dataStore: ExpressionDataStore.getInstance(), useForFetching: false, pushNewEntities: true },
        { title: "works", dataStore: WorkDataStore.getInstance(), useForFetching: false, pushNewEntities: true },

    ]

    private get pushDataStores() {
        return this.dataStores.filter(ds => ds.useForFetching);
    }

    public mounted() {
        this.fetchLastSynchroStats()
        this.getNbEntitiesInUpdateCache()
        dataSynchroEventBus$.subscribe(
            async event => {
                if (event.status === DataSynchronisationStatus.SUCCESS) {
                    this.nbSynchroCompleted++;
                    // All sychros are completed
                    if (this.nbSynchroCompleted === this.nbSubSynchros) {
                        await this.saveGlobalStats(DataSynchronisationStatus.SUCCESS)
                        this.globalSyncing = false;
                    }
                } else if (event.status === DataSynchronisationStatus.ERROR) {
                    this.errorInGlobalSynchro = true;
                    this.progressBarVariant = "danger";
                    await this.saveGlobalStats(event.status)
                    this.globalSyncing = false;
                }
            }
        )
    }

    public open() {
        this.$refs.modal.show()
        this.getNbEntitiesInUpdateCache()
        // Need to refresh time-based properties
        this.$forceUpdate();
    }

    // Fetching data
    public syncAll() {
        this.executionStartTime = moment.now();
        this.globalSyncing = true;
        this.nbSynchroCompleted = 0;
        this.progressBarVariant = "success";
        this.errorInGlobalSynchro = false;
        for (const dataStore of this.pushDataStores) {
            dataStore.dataStore.synchronize(0, true, true)
        }
    }

    public async abortAll() {
        await this.saveGlobalStats(DataSynchronisationStatus.ABORTED)
        this.progressBarVariant = "danger";
        for (const dataStore of this.pushDataStores) {
            dataStore.dataStore.abortSynchronisation()
        }
        this.globalSyncing = false;
    }

    private async saveGlobalStats(status: DataSynchronisationStatus) {
        // Saving global sync stats to cache
        await OfflineDataFetcher.saveGlobalSyncStats({
            currentNbSychronized: 0,
            lastSynchroDate: moment.now(),
            lastSynchroDuration: moment().diff(this.executionStartTime),
            lastSynchroStatus: status,
            maxNbToSynchronize: 0,
        })
        this.fetchLastSynchroStats()
    }

    /**
     * Retrieve last synchro and push stats from cache
     */
    private async fetchLastSynchroStats() {
        this.lastSynchroStats = await OfflineDataFetcher.getLastGlobalSyncStats()
        this.lastPushStats = await OfflineDataUpdateCacher.getLastPushStats();
    }

    private get lastSynchroDuration() {
        return formatDuration(
            this.lastSynchroStats?.lastSynchroDuration,
            this.$t("time.minutes"),
            this.$t("time.seconds")
        ) || "..."
    }

    private get lastSynchroDate() {
        const formattedDate = formatDateFromNow(this.lastSynchroStats?.lastSynchroDate, this.$i18n.locale)
        return formattedDate !== "" ? formattedDate || "..." : "...";
    }

    private get lastSynchroStatus() {
        return this.lastSynchroStats?.lastSynchroStatus ?
            this.$t(`state.${this.lastSynchroStats.lastSynchroStatus}`) :
            "...";
    }

    private get lastPushStatus() {
        return this.lastPushStats?.status ?
            this.$t(`state.${this.lastPushStats.status}`) :
            "...";
    }


    private get formattedLastPushDate() {
        const formattedDate = formatDateFromNow(this.lastPushStats?.date, this.$i18n.locale)
        return formattedDate !== "" ? formattedDate : "...";
    }

    private statusIcon(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return faCheck;
            } else {
                return faTimes;
            }
        }
        return null;
    }

    private statusIndicatorStyle(status: DataSynchronisationStatus) {
        if (status) {
            if (status === DataSynchronisationStatus.SUCCESS) {
                return "status-success";
            } else {
                return "status-error";
            }
        }
        return "";
    }

    private get nbSubSynchros() {
        return this.pushDataStores.length
    }


    private async getNbEntitiesInUpdateCache() {
        this.nbEntitiesInUpdateCache = 0;
        for (const dataStore of this.dataStores) {
            await dataStore.dataStore.fetchNbEntitiesInUpdateCache();
            this.nbEntitiesInUpdateCache += dataStore.dataStore.getNbEntitiesInUpdateCache
        }
    }

    private get nbDataPushers() {
        return this.dataStores.length;
    }

    // Uploading data
    private async pushAll() {
        this.nbSuccesfullPushes = 0;
        this.nbPushesCompleted = 0;
        let totalNbInCache = 0;

        this.globalPushing = true;
        this.pushResults = [];
        const pushObservables: Array<Observable<boolean>> = [];
        // Creating an array of observables, one for each entity to push
        for (const dataStore of this.dataStores) {
            totalNbInCache += await dataStore.dataStore.getNbCachedObjects()
            pushObservables.push(await dataStore.dataStore.pushCachedValues(dataStore.pushNewEntities));
        }

        // Subscribe to all observables and figure out global push status
        merge(...pushObservables)
            .subscribe(async updateSuccess => {
                this.nbPushesCompleted++;
                if (updateSuccess) {
                    this.nbSuccesfullPushes++;
                }

                // All updates have been performed (with success or not)
                if (this.nbPushesCompleted === totalNbInCache) {
                    if (this.nbPushesCompleted !== this.nbSuccesfullPushes) {
                        this.pushAllStatus = DataSynchronisationStatus.ERROR;
                        this.pushProgressBarVariant = "danger";
                    } else {
                        this.pushAllStatus = DataSynchronisationStatus.SUCCESS;
                        this.pushProgressBarVariant = "sucess";
                    }
                    this.getNbEntitiesInUpdateCache()
                    // Saving push stats
                    const now = moment.now();
                    await OfflineDataUpdateCacher.setLastPushStats(now, this.pushAllStatus);
                    this.fetchLastSynchroStats()
                    this.globalPushing = false;
                }
            })
    }

    private abortPushAll() {
        this.globalPushing = false;
        for (const dataStore of this.dataStores) {
            dataStore.dataStore.abortPush();
        }
    }

    @Watch("globalSyncing")
    private onGlobalSyncingChange() {
        if (!this.globalSyncing) {
            this.$emit("sync-finished");
        }
    }

    @Watch("globalPushing")
    private onGlobalPushingChange() {
        if (!this.globalPushing) {
            this.$emit("sync-finished");
            this.emitOnBus('push-synchro-performed')
        }
    }
}